import React, { useState, useEffect, useCallback } from 'react';
import { broadcastsAPI } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { PRIVILEGES } from '../config/roles';
import { formatIstDateTime } from '../utils/formatIst';
import {
  Megaphone, Send, Clock, CalendarClock, Trash2, Power,
  Users, UserCheck, AlertCircle, X,
} from 'lucide-react';

const AUDIENCE_OPTIONS = [
  { value: 'all_reps', label: 'All sales reps', icon: UserCheck },
  { value: 'reps_not_punched_in', label: "Reps who haven't punched in today", icon: AlertCircle },
  { value: 'all_staff', label: 'All staff (admin / manager / supervisor)', icon: Users },
  { value: 'everyone', label: 'Everyone (staff + reps)', icon: Megaphone },
];

const AUDIENCE_LABELS = AUDIENCE_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const SCHEDULE_OPTIONS = [
  { value: 'immediate', label: 'Send now', icon: Send },
  { value: 'once', label: 'Schedule for date & time', icon: CalendarClock },
  { value: 'daily', label: 'Repeat daily at a time', icon: Clock },
];

const emptyForm = () => ({
  title: '',
  message: '',
  audience: 'all_reps',
  schedule_type: 'immediate',
  scheduled_at: '',
  run_time: '09:30',
});

const statusBadge = (status) => {
  const map = {
    sent: 'bg-green-100 text-green-700',
    scheduled: 'bg-blue-100 text-blue-700',
    cancelled: 'bg-gray-100 text-gray-600',
    failed: 'bg-red-100 text-red-700',
  };
  return map[status] || 'bg-gray-100 text-gray-600';
};

const Announcements = () => {
  const { hasAllPrivileges } = useAuth();
  const canManage = hasAllPrivileges([PRIVILEGES.MANAGE_NOTIFICATIONS]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await broadcastsAPI.getAll();
      setItems(res.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load announcements');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setResult(null);
    if (!form.title.trim()) {
      setResult({ error: 'Title is required.' });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        message: form.message.trim(),
        audience: form.audience,
        schedule_type: form.schedule_type,
        category: 'announcement',
      };
      if (form.schedule_type === 'once') {
        if (!form.scheduled_at) {
          setResult({ error: 'Please choose a date and time.' });
          setSubmitting(false);
          return;
        }
        payload.scheduled_at = new Date(form.scheduled_at).toISOString();
      } else if (form.schedule_type === 'daily') {
        payload.run_time = form.run_time;
      }

      const res = await broadcastsAPI.create(payload);
      setResult({ success: res.data?.message || 'Done.' });
      setForm(emptyForm());
      load();
    } catch (err) {
      setResult({ error: err.response?.data?.message || err.message || 'Failed to send announcement' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendNow = async (id) => {
    try {
      const res = await broadcastsAPI.sendNow(id);
      setResult({ success: res.data?.message || 'Sent.' });
      load();
    } catch (err) {
      setResult({ error: err.response?.data?.message || err.message || 'Failed to send' });
    }
  };

  const handleToggleActive = async (item) => {
    try {
      await broadcastsAPI.update(item.id, { is_active: item.is_active ? 0 : 1 });
      load();
    } catch (err) {
      setResult({ error: err.response?.data?.message || err.message || 'Failed to update' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this notification? This cannot be undone.')) return;
    try {
      await broadcastsAPI.remove(id);
      load();
    } catch (err) {
      setResult({ error: err.response?.data?.message || err.message || 'Failed to delete' });
    }
  };

  const renderWhen = (item) => {
    if (item.schedule_type === 'daily') return `Daily at ${item.run_time}`;
    if (item.schedule_type === 'once') return formatIstDateTime(item.scheduled_at);
    return formatIstDateTime(item.created_at);
  };

  const submitLabel = () => {
    if (submitting) return 'Working…';
    if (form.schedule_type === 'immediate') return 'Send announcement';
    if (form.schedule_type === 'daily') return 'Schedule daily';
    return 'Schedule';
  };

  return (
    <div className="admin-page space-y-6 pb-10">
      <div className="admin-page-header">
        <div className="admin-page-header-title">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-blue-600" /> Announcements & Reminders
          </h2>
          <p className="text-gray-600 mt-1">
            Send push notifications to staff and reps. The daily punch-in reminder runs automatically.
          </p>
        </div>
      </div>

      {result && (
        <div
          className={`rounded-lg p-4 flex items-start justify-between ${
            result.error ? 'bg-red-50 border border-red-300 text-red-700' : 'bg-green-50 border border-green-300 text-green-700'
          }`}
        >
          <span>{result.error || result.success}</span>
          <button onClick={() => setResult(null)} className="text-current opacity-60 hover:opacity-100">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Compose */}
        {canManage && (
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">New notification</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="e.g., Team meeting at 5 PM"
                    maxLength={160}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Message</label>
                  <textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Details of the announcement…"
                    rows={3}
                    maxLength={500}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Audience</label>
                  <select
                    value={form.audience}
                    onChange={(e) => setForm({ ...form, audience: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    {AUDIENCE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Delivery</label>
                  <div className="space-y-2">
                    {SCHEDULE_OPTIONS.map((o) => {
                      const Icon = o.icon;
                      const active = form.schedule_type === o.value;
                      return (
                        <button
                          type="button"
                          key={o.value}
                          onClick={() => setForm({ ...form, schedule_type: o.value })}
                          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition ${
                            active
                              ? 'border-blue-500 bg-blue-50 text-blue-700 font-semibold'
                              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <Icon className="w-4 h-4" /> {o.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {form.schedule_type === 'once' && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Date & time</label>
                    <input
                      type="datetime-local"
                      value={form.scheduled_at}
                      onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}

                {form.schedule_type === 'daily' && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Time (server time)</label>
                    <input
                      type="time"
                      value={form.run_time}
                      onChange={(e) => setForm({ ...form, run_time: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-blue-600 text-white py-2 rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50"
                >
                  {submitLabel()}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* History + scheduled */}
        <div className={canManage ? 'lg:col-span-2 space-y-6' : 'lg:col-span-3 space-y-6'}>
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">History & scheduled</h3>
            {loading ? (
              <div className="text-center text-gray-600 py-8">Loading…</div>
            ) : error ? (
              <div className="text-center text-red-600 py-8">{error}</div>
            ) : items.length === 0 ? (
              <p className="text-gray-600 text-center py-8">No notifications yet</p>
            ) : (
              <div className="admin-table-scroll">
                <table className="admin-data-table text-sm">
                  <thead className="bg-gray-100 border-b">
                    <tr>
                      <th className="px-4 py-2 text-left font-semibold">Title</th>
                      <th className="px-4 py-2 text-left font-semibold">Audience</th>
                      <th className="px-4 py-2 text-left font-semibold">When</th>
                      <th className="px-4 py-2 text-left font-semibold">Status</th>
                      <th className="px-4 py-2 text-right font-semibold">Sent to</th>
                      {canManage && <th className="px-4 py-2 text-right font-semibold">Actions</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {items.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50 transition">
                        <td className="px-4 py-2">
                          <div className="font-semibold text-gray-900">{item.title}</div>
                          {item.message && (
                            <div className="text-xs text-gray-500 max-w-xs truncate">{item.message}</div>
                          )}
                          {item.category === 'attendance_reminder' && (
                            <span className="inline-block mt-1 text-[10px] uppercase tracking-wide bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                              Auto reminder
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-gray-700">{AUDIENCE_LABELS[item.audience] || item.audience}</td>
                        <td className="px-4 py-2 text-gray-700">{renderWhen(item)}</td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(item.status)}`}>
                            {item.schedule_type === 'daily' ? (item.is_active ? 'active' : 'paused') : item.status}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right text-gray-700">
                          {item.recipients_count != null ? item.recipients_count : '—'}
                        </td>
                        {canManage && (
                          <td className="px-4 py-2">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => handleSendNow(item.id)}
                                title="Send now"
                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                              >
                                <Send className="w-4 h-4" />
                              </button>
                              {item.schedule_type === 'daily' && (
                                <button
                                  onClick={() => handleToggleActive(item)}
                                  title={item.is_active ? 'Pause' : 'Resume'}
                                  className={`p-1.5 rounded ${item.is_active ? 'text-amber-600 hover:bg-amber-50' : 'text-green-600 hover:bg-green-50'}`}
                                >
                                  <Power className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={() => handleDelete(item.id)}
                                title="Delete"
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Announcements;
